'use client';
import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Phone, Eye, EyeOff, Loader2, AlertCircle, Building2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface LoginFormValues {
  phone: string;
  pin: string;
}

interface GymPortalLoginFormProps {
  onForgotPin: () => void;
  onSignup: () => void;
  onBack: () => void;
}

export default function GymPortalLoginForm({ onForgotPin, onSignup, onBack }: GymPortalLoginFormProps) {
  const [showPin, setShowPin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lockInfo, setLockInfo] = useState<{ locked: boolean; message: string } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const pinRef = useRef<HTMLInputElement | null>(null);

  const { register, handleSubmit, formState: { errors }, watch } = useForm<LoginFormValues>();
  const phone = watch('phone', '');
  const pinValue = watch('pin', '');

  const { ref: pinRegRef, ...pinRegRest } = register('pin', {
    required: 'PIN is required',
    minLength: { value: 4, message: 'PIN must be 4 digits' },
    maxLength: { value: 4, message: 'PIN must be 4 digits' },
    pattern: { value: /^\d{4}$/, message: 'PIN must be 4 numeric digits' },
  });

  const onSubmit = async (data: LoginFormValues) => {
    if (lockInfo?.locked) {
      toast.error(lockInfo.message);
      return;
    }

    setIsLoading(true);
    setErrorMsg(null);
    try {
      const response = await fetch('/api/auth/pin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: data.phone, pin: data.pin, portalType: 'gym' }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.locked) {
          setLockInfo({ locked: true, message: result.error });
        }
        if (result.redirect_to_member) {
          setErrorMsg('Members must use the Member Portal to sign in.');
        } else {
          setErrorMsg(result.error || 'Login failed. Please try again.');
        }
        setIsLoading(false);
        return;
      }

      if (!result.success) {
        setErrorMsg(result.error || 'Login failed. Please try again.');
        setIsLoading(false);
        return;
      }

      const supabase = createClient();

      if (result.session?.access_token && result.session?.refresh_token) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });

        if (sessionError) {
          setErrorMsg('Authentication error. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      toast.success(`Welcome back, ${result.profile?.full_name || 'User'}!`);

      const role = result.profile?.role;
      if (role === 'owner') {
        window.location.href = '/owner-dashboard';
      } else if (role === 'staff' || role === 'trainer') {
        window.location.href = '/staff';
      } else {
        window.location.href = '/owner-dashboard';
      }

    } catch (err) {
      console.error('Login error:', err);
      setErrorMsg('Login failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-5"
      >
        ← Back to Portal Selection
      </button>

      <div className="flex items-center gap-2.5 mb-5">
        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
          <Building2 size={18} className="text-primary" />
        </div>
        <div>
          <h2 className="font-display text-lg text-foreground font-bold">Gym Portal</h2>
          <p className="text-xs text-muted-foreground">Owner · Staff · Trainer</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        {/* Phone */}
        <div>
          <label htmlFor="gym-phone" className="block text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">
            Phone Number
          </label>
          <div className="relative">
            <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="gym-phone"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="9876543210"
              autoComplete="tel"
              className="w-full bg-input border border-border rounded-xl pl-9 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all"
              {...register('phone', {
                required: 'Phone number is required',
                pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number' },
              })}
            />
          </div>
          {errors.phone && <p className="mt-1 text-xs text-risk">{errors.phone.message}</p>}
        </div>

        {/* PIN — native input, no custom keypad */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="gym-pin" className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
              4-Digit PIN
            </label>
            <button
              type="button"
              onClick={() => {
                setShowPin(v => !v);
                setTimeout(() => pinRef.current?.focus(), 50);
              }}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPin ? <EyeOff size={12} /> : <Eye size={12} />}
              {showPin ? 'Hide' : 'Show'}
            </button>
          </div>

          {/* PIN dot display */}
          <div className="flex gap-3 justify-center mb-3 pointer-events-none select-none">
            {[0, 1, 2, 3].map(i => (
              <div
                key={`gym-pin-dot-${i}`}
                className={`w-12 h-14 rounded-xl border-2 flex items-center justify-center text-xl font-bold transition-all duration-150 ${
                  (pinValue?.length ?? 0) > i ? 'border-primary bg-primary/10' : 'border-border bg-input'
                }`}
              >
                {(pinValue?.length ?? 0) > i ? (showPin ? pinValue[i] : '●') : ''}
              </div>
            ))}
          </div>

          {/* Native hidden input — captures keyboard input */}
          <input
            id="gym-pin"
            type={showPin ? 'text' : 'password'}
            inputMode="numeric"
            maxLength={4}
            autoComplete="current-password"
            placeholder="Enter 4-digit PIN"
            className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring transition-all text-center tracking-[0.5em]"
            disabled={!!lockInfo?.locked}
            {...pinRegRest}
            ref={(el) => {
              pinRegRef(el);
              pinRef.current = el;
            }}
            onChange={(e) => {
              // Only allow digits
              e.target.value = e.target.value.replace(/\D/g, '').slice(0, 4);
              pinRegRest.onChange(e);
              setErrorMsg(null);
            }}
          />
          {errors.pin && <p className="mt-1 text-xs text-risk">{errors.pin.message}</p>}
          <p className="mt-1 text-xs text-muted-foreground">Use your keyboard or numeric keypad to enter your PIN.</p>
        </div>

        {(errorMsg || lockInfo?.locked) && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-risk/10 border border-risk/30">
            <AlertCircle size={15} className="text-risk shrink-0 mt-0.5" />
            <p className="text-sm text-risk">{lockInfo?.locked ? lockInfo.message : errorMsg}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || !!lockInfo?.locked || !phone}
          className="w-full h-12 gradient-primary text-white text-sm font-bold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-[0.98]"
        >
          {isLoading ? <><Loader2 size={16} className="animate-spin" /> Signing in...</> : 'Sign In to Gym Portal'}
        </button>

        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={onForgotPin}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Forgot PIN?
          </button>
          <button
            type="button"
            onClick={onSignup}
            className="text-xs text-secondary hover:text-secondary/80 font-semibold transition-colors"
          >
            Register Your Gym →
          </button>
        </div>
      </form>
    </div>
  );
}
